import React, { FC, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import RemoveItems from '~Components/RemoveItems';
import Modal from '~ComponentsImproved/Modal';
import NumberInput from '~ComponentsImproved/Numpad';
import { useI18n, useWaste } from '~Hooks';
import { itemInBong } from '~Scenes/POS/Queries/__queries__';
import { ITEM_FRAGMENT } from '~Scenes/POS/Queries/query';
import { useInventoryUniverse } from '~Components/InventoryUniverse';

interface Props {
  itemId: string;
  goBack: () => void;
  handleCancel: () => void;
}

/** If the user goes to an individual product to register waste */
const RegisterWaste: FC<Props> = ({ itemId, goBack, handleCancel }) => {
  const { i18n } = useI18n();

  const waste = useWaste();
  const client = useApolloClient();
  const inventoryUniverse = useInventoryUniverse();
  const invItem = inventoryUniverse.items.find((i) => i.itemId === itemId);

  const [quantity, setQuantity] = useState<number | null>(null);

  const itemFragment = client.readFragment<itemInBong>({
    id: `Item${itemId}`,
    fragment: ITEM_FRAGMENT,
  });

  const shouldAskForUnits = Boolean(itemFragment?.volumeSuffix);

  return (
    <>
      {quantity ? (
        <Modal isShowing={true} hide={() => goBack()}>
          <RemoveItems
            isPOS
            onComplete={(message, commentOnWaste) => {
              const itemsToWaste = shouldAskForUnits
                ? [
                    {
                      itemId,
                      title: `${quantity}${itemFragment?.volumeSuffix} ${i18n(
                        itemFragment?.titleLang,
                      )}`,
                      fraction: 1,
                    },
                  ]
                : Array.from({ length: quantity! }, () => ({
                    itemId,
                    /** We don't allow entering decimal products - therefore 1 as fraction */
                    fraction: 1,
                  }));
              waste.register(itemsToWaste, message, commentOnWaste || '');
              handleCancel();
            }}
          />
        </Modal>
      ) : (
        <NumberInput
          title={
            shouldAskForUnits
              ? `${i18n('POS.HowMany')} ${itemFragment?.volumeSuffix}?`
              : i18n('Waste.NumberOfProducts')
          }
          onSubmit={(val) => setQuantity(Number(val))}
          initialValue="1"
          initialValueIsPlaceholder
          decimal={true}
          maxValue={invItem && typeof invItem.count === 'number' ? invItem.count : undefined}
          onClose={() => goBack()}
        />
      )}
    </>
  );
};

export default RegisterWaste;
